import { cn } from '../lib/utils';

const variants: Record<string, { badge: string; dot: string }> = {
  default: {
    badge: 'bg-surface-muted text-text',
    dot: 'bg-text-muted',
  },
  success: {
    badge: 'bg-success-soft text-success',
    dot: 'bg-success',
  },
  warning: {
    badge: 'bg-warning-soft text-warning',
    dot: 'bg-warning',
  },
  danger: {
    badge: 'bg-danger-soft text-danger',
    dot: 'bg-danger',
  },
  info: {
    badge: 'bg-info-soft text-info',
    dot: 'bg-info',
  },
  brand: {
    badge: 'bg-brand-soft text-brand',
    dot: 'bg-brand',
  },
};

export function Badge({
  variant = 'default',
  className,
  children,
}: {
  variant?: keyof typeof variants;
  className?: string;
  children: React.ReactNode;
}) {
  const v = variants[variant] ?? variants.default;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium capitalize',
        v.badge,
        className,
      )}
    >
      <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', v.dot)} aria-hidden />
      {children}
    </span>
  );
}

const STATUS_MAP: Record<string, keyof typeof variants> = {
  delivered: 'success',
  sent: 'success',
  approved: 'success',
  active: 'success',
  healthy: 'success',
  completed: 'success',
  paid: 'success',
  resolved: 'success',
  pending: 'warning',
  queued: 'warning',
  processing: 'warning',
  scheduled: 'info',
  submitted: 'info',
  open: 'info',
  draft: 'default',
  cancelled: 'default',
  disabled: 'default',
  failed: 'danger',
  rejected: 'danger',
  suspended: 'danger',
  overdue: 'danger',
  expired: 'danger',
  degraded: 'warning',
  unavailable: 'danger',
};

function formatStatusLabel(status: string) {
  return status.replace(/_/g, ' ');
}

export function StatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  return (
    <Badge variant={STATUS_MAP[normalized] ?? 'default'}>
      {formatStatusLabel(normalized)}
    </Badge>
  );
}
